import { Injectable } from '@angular/core';
import { AutosuggestApiService } from '@shared/api';
import { Pagination } from '@ui-kit/miscs';
import { AutoComplete } from 'primeng/autocomplete';

export interface AutocompleteSettings {
    id: string;
    fluid?: boolean;
    maxLength: number | null;
    forceSelection: boolean;
    optionLabel: string;
    optionValue: string;
    lazy: boolean;
    showClear: boolean;
    required: boolean;
    isUser?: boolean;
    emitOnHide: boolean;
    emitOnBlur: boolean;
    multiple: boolean;
    panelClass: string;
    getSuggestions: (value: string, pagination: Pagination) => Promise<any[]>;
}

export const AUTOCOMPLETE_FETCH_LIMIT = 30;

@Injectable({ providedIn: 'root' })
export class AutocompleteService {
    private activeAutocomplete: AutoComplete | null = null;

    constructor(private autosuggestApi: AutosuggestApiService) {}

    setActiveAutocomplete(autocomplete: AutoComplete): void {
        if (this.activeAutocomplete && this.activeAutocomplete !== autocomplete) {
            this.activeAutocomplete.hide();
        }

        this.activeAutocomplete = autocomplete;
    }

    clearActiveAutocomplete(autocomplete: AutoComplete): void {
        if (this.activeAutocomplete === autocomplete) {
            this.activeAutocomplete = null;
        }
    }

    user(): AutocompleteSettings {
        return {
            id: 'user-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'name',
            optionValue: 'name',
            lazy: true,
            showClear: true,
            required: false,
            isUser: true,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'user-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestUsers({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    organization(): AutocompleteSettings {
        return {
            id: 'organization-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'name',
            optionValue: 'name',
            lazy: true,
            showClear: true,
            required: false,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'organization-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestOrganizations({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    laboratory(): AutocompleteSettings {
        return {
            id: 'laboratory-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'name',
            optionValue: 'name',
            lazy: true,
            showClear: true,
            required: false,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'laboratory-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestLaboratories({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    assayDesigner(required = false): AutocompleteSettings {
        return {
            id: 'assay-designer-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: true,
            optionLabel: 'name',
            optionValue: 'name',
            lazy: true,
            showClear: true,
            required: required,
            isUser: true,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'assay-designer-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestAssayDesigners({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    strain(): AutocompleteSettings {
        return {
            id: 'strain-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'name',
            optionValue: 'name',
            lazy: true,
            showClear: true,
            required: false,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'strain-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestStrains({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    predefinedProbe(): AutocompleteSettings {
        return {
            id: 'predefined-probe-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: true,
            optionLabel: 'predefinedProbeName',
            optionValue: '',
            lazy: true,
            showClear: true,
            required: true,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'predefined-probe-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestPredefinedProbes({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    predefinedProbeForIncompatibilityForAdd(): AutocompleteSettings {
        return {
            id: 'predefined-probe-for-incompatibility-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'text',
            optionValue: 'value',
            lazy: true,
            showClear: false,
            required: true,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: true,
            panelClass: 'predefined-probe-for-incompatibility-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestPredefinedProbesForIncompatibilityAdd({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    incompatibleProbesGroups(): AutocompleteSettings {
        return {
            id: 'incompatible-probes-groups-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'text',
            optionValue: 'value',
            lazy: true,
            showClear: true,
            required: false,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'incompatible-probes-groups-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestIncompatibleGroupNames({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }

    incompatibleProbeNames(): AutocompleteSettings {
        return {
            id: 'incompatible-probe-names-autocomplete',
            maxLength: 300,
            fluid: true,
            forceSelection: false,
            optionLabel: 'text',
            optionValue: 'value',
            lazy: true,
            showClear: true,
            required: false,
            isUser: false,
            emitOnHide: true,
            emitOnBlur: false,
            multiple: false,
            panelClass: 'incompatible-probe-names-autocomplete-panel',
            getSuggestions: async (value: string, pagination?: Pagination) => {
                const response = await this.autosuggestApi.suggestIncompatibleProbes({
                    searchValue: value?.trim() ?? '',
                    take: AUTOCOMPLETE_FETCH_LIMIT,
                    skip: pagination?.skip ?? 0,
                });
                return response.data ?? [];
            },
        };
    }
}
